import { MeetRepository } from '../domain';
import { FeedbackData, FeedbackSendParams, Meet, MeetAction, Tenant } from '../domain/types';

const API_VIDEO_URL: string | undefined = import.meta.env.VITE_API_VIDEO_CONSULTATION
	? import.meta.env.VITE_API_VIDEO_CONSULTATION
	: globalThis.import_meta_env.VITE_API_VIDEO_CONSULTATION;

const apiVideoPath = `${API_VIDEO_URL}/apivideo`;

export class ApiMeetRepository implements MeetRepository {
	private readonly loadTenantPath = apiVideoPath;
	private readonly videoConsultationPath = `${apiVideoPath}/$code`;
	private readonly feedbackPath = `${this.videoConsultationPath}/feedback`;

	async loadTenant(): Promise<Tenant> {
		const response = await fetch(this.loadTenantPath);
		if (!response.ok) {
			const error = await response.json();
			throw new Error(error);
		}
		return (await response.json()) as Tenant;

	}

	async loadMeet(code: string, tenant?: string): Promise<Meet> {
		let url = this.videoConsultationPath.replace('$code', code);

		if (tenant) {
			url = `${url}/tenant/$tenant`.replace('$tenant', tenant);
		}

		const response = await fetch(url);
		if (!response.ok) {
			const error = await response.json();
			throw new Error(error);
		}

		return (await response.json()) as Meet;
	}

	async _sendAction({
						  action,
						  code,
						  videoId,
						  memberId,
						  token,
					  }: {
		action: MeetAction;
		code: string;
		videoId: string;
		memberId: string;
		token: string;
	}): Promise<void> {
		await fetch(this.videoConsultationPath.replace('$code', code), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				action,
				videoId,
				memberId,
				token,
			}),
		});
	}

	async join(meet: Meet): Promise<void> {
		this._sendAction({
			action: 'join',
			code: meet.code,
			videoId: meet.videoId,
			memberId: meet.memberId,
			token: meet.connection.token,
		});
	}

	async waiting(meet: Meet): Promise<void> {
		await this._sendAction({
			action: 'waiting',
			code: meet.code,
			videoId: meet.videoId,
			memberId: meet.memberId,
			token: meet.connection.token,
		});
	}

	async hangUpAndFinish(meet: Meet): Promise<void> {
		await this._sendAction({
			action: 'hang',
			code: meet.code,
			videoId: meet.videoId,
			memberId: meet.memberId,
			token: meet.connection.token,
		});
	}

	async retrieveFeedbackData(code: string): Promise<FeedbackData> {
		let url = this.feedbackPath.replace('$code', code);

		const response = await fetch(url);

		if (!response.ok) {
			const error = await response.json();

			if (response.status === 409) {
				throw new Error('FEEDBACK_ALREADY_EXISTS');
			}

			throw new Error(JSON.stringify(error));
		}

		const data = await response.json();

		return data as FeedbackData;
	}

	async sendFeedback({
		code,
		feedback,
		videoId,
		memberId,
		token,
	}: FeedbackSendParams): Promise<Response> {
		return fetch(this.feedbackPath.replace('$code', code), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				reaction: feedback,
				videoId,
				memberId,
				token,
			}),
		});
	}
}
